import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

interface Note {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
}

interface ClienteAnotacoesTabProps {
  customerId: string;
  companyId: string;
}

export function ClienteAnotacoesTab({ customerId, companyId }: ClienteAnotacoesTabProps) {
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState('');

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['customer-notes', customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customer_notes')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Note[];
    }
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('customer_notes')
        .insert({
          customer_id: customerId,
          company_id: companyId,
          user_id: user.id,
          content: newNote.trim()
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', customerId] });
      setNewNote('');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await supabase
        .from('customer_notes')
        .delete()
        .eq('id', noteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', customerId] });
    }
  });

  return (
    <div className="space-y-6">
      {/* New Note */}
      <div className="bg-card border rounded-xl p-4 space-y-3">
        <textarea 
          className="w-full min-h-[100px] p-3 border rounded-lg resize-none bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Adicione uma anotação sobre este cliente..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
        />
        <div className="flex justify-end">
          <Button 
            onClick={() => createMutation.mutate()}
            disabled={!newNote.trim() || createMutation.isPending}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Adicionar Anotação
          </Button>
        </div>
      </div>

      {/* Notes List */}
      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground">Carregando...</div>
      ) : notes.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground italic bg-muted/30 rounded-lg">
          Nenhuma anotação registrada.
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div 
              key={note.id} 
              className="bg-card border rounded-xl p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-[hsl(var(--basic-100))] flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-[hsl(var(--basic-600))]" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {new Date(note.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className="text-sm text-[hsl(var(--basic-800))] whitespace-pre-wrap">{note.content}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => deleteMutation.mutate(note.id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
